/**
 * Os PDFs de etapa na área do cliente — os MESMOS do desktop.
 *
 * OPU, FIV (clivagem e embriões) e TE chamam os geradores de `workflowPDF`,
 * os mesmos botões do laboratório. O que muda é a fonte: o desktop lê as
 * sessões direto das tabelas; o cliente recebe de `workflow_do_servico` a
 * chave `pdf`, já filtrada para o serviço dele — estágios e destinos do PDF
 * de embriões são recontados sobre os embriões DESTE serviço, não sobre a FIV
 * inteira. Aqui só se traduz para o formato que cada gerador espera.
 *
 * O texto vem do banco e o gerador escreve HTML numa janela da origem do
 * portal; por isso tudo passa por `escProfundo` antes. No desktop quem digita
 * e quem imprime são a mesma equipe; aqui não.
 *
 * DG30 e DG60 não têm PDF no desktop. Para elas fica a folha própria abaixo,
 * no mesmo papel (`baseCSS`, `serviceHeader`).
 */
import { assinaturaDoRodape, linhaDoResponsavel } from './laboratorioNoRelatorio';
import {
  baseCSS, serviceHeader, openPrint, fmt,
  generateOPUPDF, generateFIVClivagemPDF, generateFIVEmbryosPDF, generateTEPDF,
} from './workflowPDF';
import { embrioesDaTE } from '../data/embryos';

const esc = (v) => String(v ?? '').replace(/[&<>"']/g, c => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

/** Escapa toda string de um objeto, mantendo números, booleanos e nulos. */
export const escProfundo = (v) => {
  if (typeof v === 'string') return esc(v);
  if (Array.isArray(v)) return v.map(escProfundo);
  if (v && typeof v === 'object') {
    return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, escProfundo(x)]));
  }
  return v;
};

/** O `service` que o cabeçalho dos geradores lê. */
export const servicoParaPDF = (servico, cliente) => ({
  client_name: esc(cliente || ''),
  service_code: esc(servico?.service_code || ''),
  date: servico?.service_date,
});

/**
 * A TE no formato de `generateTEPDF`: lista de embriões e mapa de
 * transferências pelo embrião. Transferência lançada sem embrião vinculado
 * entra com a própria chave, para não sumir do papel.
 */
export const teParaPDF = (te) => {
  const linhas = te?.linhas || [];
  const embrioes = linhas.map(l => ({
    id: l.key,
    service_number: l.service_number,
    stage: l.stage,
    bull_name: l.bull_name,
    donor: { identifier: l.donor_identifier, rgd: l.donor_rgd },
  }));
  const transferencias = Object.fromEntries(linhas.map(l => [l.key, {
    status: l.status,
    recipient: { identifier: l.recipient },
    pre_evaluation: { corpus_luteum: l.corpus_luteum, cl_quality: l.cl_quality },
    dates: { expected_dg30: l.expected_dg30 },
    observations: l.observations,
  }]));
  return { embrioes: embrioesDaTE(embrioes, transferencias), transferencias };
};

/* ---------------------------------------------------------------- DG */

const vazio = (colunas) =>
  `<tr><td colspan="${colunas}" style="text-align:center;color:#94a3b8;padding:12px;font-style:italic">Sem detalhes lançados nesta etapa.</td></tr>`;
const cel = (v) => (v != null && v !== '' ? esc(v) : '<span class="dim">—</span>');

/** A folha da DG. Separada de `imprimirPrenhez` para testar sem janela. */
export function htmlDaPrenhez({ conteudo, etapa, servico, cliente }) {
  const { linhas = [], prenhas = [] } = conteudo || {};
  const corpo = linhas.map(p => {
    const prenhe = (p.resultado || '').toUpperCase().startsWith('PRENH');
    return `<tr>
      <td>${cel(p.receptora)}</td>
      <td>${p.embriao != null ? `#${esc(p.embriao)}` : '—'}</td>
      <td class="${prenhe ? 'ok' : 'no'}">${esc((p.resultado || 'aguardando').toLowerCase())}</td>
      <td>${p.data ? fmt(p.data) : '—'}</td></tr>`;
  }).join('') || vazio(4);

  return `<style>${baseCSS()}</style>
  ${serviceHeader(servicoParaPDF(servico, cliente))}
  <div class="section-title">${esc(etapa?.rotulo || '')} — Diagnóstico de Gestação</div>
  <div class="session-info">
    <span><b>Data:</b> ${fmt(etapa?.data)}</span>
    <span><b>Prenhes:</b> <strong>${prenhas.length}</strong> &nbsp;|&nbsp; <b>Receptoras:</b> ${linhas.length}</span>
  </div>
  <table>
    <thead><tr><th>Receptora</th><th>Embrião</th><th>Resultado</th><th>Data</th></tr></thead>
    <tbody>${corpo}</tbody>
    <tfoot><tr>
      <td colspan="2" style="text-align:right">Prenhes / Receptoras:</td>
      <td colspan="2"><strong>${prenhas.length} / ${linhas.length}</strong></td>
    </tr></tfoot>
  </table>
  <div class="footer">${assinaturaDoRodape()} · ${esc(etapa?.rotulo || '')} · ${esc(cliente || '')}${linhaDoResponsavel()}</div>`;
}

/* ----------------------------------------------------------- Botões */

/**
 * Os PDFs que a etapa oferece, com o rótulo do botão — os mesmos do desktop
 * (a FIV tem dois: Clivagem e Embriões). Lista vazia quando o banco não
 * mandou os dados da etapa.
 */
export const pdfsDaEtapa = ({ conteudo, etapa, servico, cliente, pdf }) => {
  const svc = servicoParaPDF(servico, cliente);

  if (conteudo?.tipo === 'opu' && pdf?.opu) {
    return [{ rotulo: 'PDF', gerar: () => generateOPUPDF(svc, escProfundo(pdf.opu)) }];
  }
  if (conteudo?.tipo === 'fiv' && pdf?.fiv) {
    const fiv = escProfundo(pdf.fiv);
    return [
      { rotulo: 'Clivagem', gerar: () => generateFIVClivagemPDF(svc, fiv) },
      { rotulo: 'Embriões', gerar: () => generateFIVEmbryosPDF(svc, fiv) },
    ];
  }
  if (conteudo?.tipo === 'te' && pdf?.te) {
    const te = escProfundo(pdf.te);
    const { embrioes, transferencias } = teParaPDF(te);
    return [{ rotulo: 'PDF', gerar: () => generateTEPDF(svc, te.session, embrioes, transferencias) }];
  }
  if (conteudo?.tipo === 'prenhez' && conteudo.linhas.length > 0) {
    return [{
      rotulo: 'PDF',
      gerar: () => openPrint(`${etapa?.rotulo || 'DG'} – ${servico?.service_code || ''}`,
        htmlDaPrenhez({ conteudo, etapa, servico, cliente })),
    }];
  }
  return [];
};
